import { useState, ReactNode } from "react";
import { Copy, Check } from "lucide-react";

interface CodeBlockProps {
  language?: string;
  filename?: string;
  children: ReactNode;
}

export function CodeBlock({ language, filename, children }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const getTextContent = (node: ReactNode): string => {
    if (typeof node === "string") return node;
    if (typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(getTextContent).join("");
    if (node && typeof node === "object" && "props" in (node as object)) {
      return getTextContent((node as { props: { children: ReactNode } }).props.children);
    }
    return "";
  };

  const handleCopy = () => {
    const text = getTextContent(children);
    navigator.clipboard.writeText(text.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-5 rounded-xl overflow-hidden border border-border bg-bg-secondary">
      {(filename || language) && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-border">
          <span className="text-xs text-text-faint font-mono">{filename || language}</span>
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? "Copied to clipboard" : "Copy code"}
            className="flex min-h-8 items-center gap-1.5 text-xs text-text-faint hover:text-text transition-colors"
          >
            {copied ? (
              <Check size={13} aria-hidden="true" />
            ) : (
              <Copy size={13} aria-hidden="true" />
            )}
            {copied ? "Copied" : "Copy"}
          </button>
          <span role="status" aria-live="polite" className="sr-only">
            {copied ? "Copied to clipboard" : ""}
          </span>
        </div>
      )}
      <pre className="p-4 overflow-x-auto text-xs leading-relaxed text-text-muted font-mono m-0">
        <code>{children}</code>
      </pre>
    </div>
  );
}
